import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  pipe, path, find, propEq, prop, T, F, toUpper
} from 'ramda'
import { propType } from 'graphql-anywhere'

import { THEME_OPTIONS } from '../../constants'
import Select from '../forms/select'

import styles from './grid.css'
import selectStyles from './grid-sorting-select.css'

import TILE_FRAGMENT from '../../../graphql/fragments/tile.gql'
import GridBody from './grid-body'
import { JSONLD } from '../jsonld'
import { createItemListLDSchema } from '../../lib/jsonld/item-list'

function getSortingOptions(sortValues) {
  return sortValues.map(value => ({
    label: value.label,
    value
  }))
}

function sortGrid(tiles, sortingValue) {
  if (!sortingValue) {
    return tiles
  }

  const sorting = path(['value', 'dir'], sortingValue)
  const sortingKey = path(['value', 'key'], sortingValue)

  const getCompareValue = tile => pipe(
    prop('sortValues'),
    find(propEq('key', sortingKey)),
    prop('value'),
    String,
    toUpper
  )(tile)

  return [...tiles].sort((a, b) => {
    if (sorting === 'DESC') {
      return getCompareValue(a) < getCompareValue(b) ? 1 : -1
    }
    return getCompareValue(a) < getCompareValue(b) ? -1 : 1
  })
}

function Grid({
  header,
  tiles,
  sortingValue,
  sortingOptions,
  setSortingValue,
  theme,
  contentType,
  subType,
  gridPosition,
  isContentItemOnMyList,
  addToMyList
}) {
  return (
    <div className={styles.gridComponentWrapper}>
      <div className={styles.gridHeaderWrapper}>
        {sortingValue && <div className={styles.newRow} />}
        {
          sortingValue && (
            <Select
              inline
              name="gridSort"
              label="Sort By"
              value={sortingValue}
              options={sortingOptions}
              onChange={setSortingValue}
              wrapperClass={styles.sortingWrapper}
              labelClass={selectStyles.labelClass}
              useKidsTheme={theme === THEME_OPTIONS.light}
              inputClass={selectStyles.inputClass}
            />
          )
        }
      </div>

      <GridBody
        tiles={tiles}
        contentType={contentType}
        subType={subType}
        theme={theme}
        gridPosition={gridPosition}
        listTitle={header}
        isContentItemOnMyList={isContentItemOnMyList}
        addToMyList={addToMyList}
      />
      <JSONLD schema={createItemListLDSchema(tiles)} type="ItemList" />
    </div>
  )
}

Grid.propTypes = {
  header: PropTypes.string,
  tiles: PropTypes.arrayOf(propType(TILE_FRAGMENT)).isRequired,
  sortingValue: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.object])
  }),
  sortingOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.object])
  })).isRequired,
  setSortingValue: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  contentType: PropTypes.string,
  subType: PropTypes.string,
  gridPosition: PropTypes.number.isRequired,
  isContentItemOnMyList: PropTypes.func,
  addToMyList: PropTypes.func
}

Grid.defaultProps = {
  header: '',
  sortingValue: null,
  contentType: null,
  subType: null,
  isContentItemOnMyList: F,
  addToMyList: T
}

const EnhancedGrid = React.memo(({ grid, grid: { subType, contentType }, ...props }) => {
  const sortingOptions = grid.uiConfig.sortBy && grid.uiConfig.sortBy.length
    ? getSortingOptions(grid.uiConfig.sortBy) : []

  const [sortingValue, setSortValue] = useState(null)
  const setSortingValue = useCallback((value) => {
    setSortValue(value)
  }, [sortingValue, setSortValue])

  const getTiles = () => {
    return sortGrid(grid.tiles || [], sortingValue || null)
  }

  return (
    <Grid
      sortingOptions={sortingOptions}
      subType={subType}
      contentType={contentType}
      sortingValue={sortingOptions.length ? (sortingValue || sortingOptions[0]) : null}
      setSortingValue={setSortingValue}
      tiles={getTiles()}
      header={grid.header}
      {...props}
    />
  )
})

EnhancedGrid.propTypes = {
  grid: PropTypes.oneOfType([PropTypes.object]).isRequired
}

export default EnhancedGrid
